import {
  Box, 
  Typography, 
  TextField, 
  Button, 
  IconButton,
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { formatDate } from 'state/helpers';
import CustomDatePicker from 'components/CustomDatePicker';
import FlexBetween from 'components/FlexBetween';
import SectionLabel from 'components/SectionLabel';
import FieldDisplay from 'components/FieldDisplay';


const BLANK_DEPENDENT = {
  dependentName: '',
  relationship: '',
  birthDate: null,
  nationalIdNumber: '',
  validSince: null,
  validUntil: null,
};


const Family = ({ 
  values, 
  handleChange, 
  handleBlur,
  setFieldValue, 
  isEditMode 
}) => {
  const dependents = values.dependents || [];

  const addDependent = () => setFieldValue('dependents', [...dependents, { ...BLANK_DEPENDENT }]);
  const removeDependent = (i) => setFieldValue('dependents', dependents.filter((_, idx) => idx !== i));
  const updateDep = (i, field, val) =>
    setFieldValue('dependents', dependents.map((d, idx) => idx === i ? { ...d, [field]: val } : d));

  if (!isEditMode) return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
        <FieldDisplay label="Emergency Contact Name" value={values.nextKinName} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
        <FieldDisplay label="Relationship" value={values.nextKinRelsn} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
        <FieldDisplay label="Contact Number" value={values.nextKinContact} />
      </Box>

      <Box sx={{ gridColumn: 'span 12' }}>
        <SectionLabel>Dependents</SectionLabel>
        {dependents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No dependents recorded.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Relationship</TableCell>
                  <TableCell>DateOfBirth</TableCell>
                  <TableCell>NationalID</TableCell>
                  <TableCell>ValidSince</TableCell>
                  <TableCell>ValidUntil</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dependents.map((dep, i) => (
                  <TableRow key={i}>
                    <TableCell>{dep.dependentName || '-'}</TableCell>
                    <TableCell>{dep.relationship || '-'}</TableCell>
                    <TableCell>{formatDate(dep.birthDate)}</TableCell>
                    <TableCell>{dep.nationalIdNumber || '-'}</TableCell>
                    <TableCell>{formatDate(dep.validSince)}</TableCell>
                    <TableCell>{formatDate(dep.validUntil)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <TextField
        fullWidth 
        size="small" 
        label="Emergency Contact Name" 
        name="nextKinName"
        value={values.nextKinName} 
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <TextField
        fullWidth 
        size="small" 
        label="Relationship" 
        name="nextKinRelsn"
        value={values.nextKinRelsn} 
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <TextField
        fullWidth 
        size="small" 
        label="Contact Number" 
        name="nextKinContact"
        value={values.nextKinContact} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />

      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <FlexBetween>
          <SectionLabel>Dependents</SectionLabel>
          <Button size="small" startIcon={<Add />} onClick={addDependent}>
            Add Dependent
          </Button>
        </FlexBetween>

        {dependents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No dependents recorded.
          </Typography>
        ) : (
          <Box 
            display="flex"
            flexDirection="column"
            gap={2} 
          >
            {dependents?.map((dep, i) => (
              <Box 
                key={i} 
                display="flex" 
                alignItems="center" 
                gap={2}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
              >                
                <Box 
                  flex={1}
                  display="grid" 
                  gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
                  gap={2}
                >
                  <TextField
                    fullWidth 
                    size="small" 
                    label="Name" 
                    value={dep.dependentName}
                    onChange={(e) => updateDep(i, 'dependentName', e.target.value)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  />
                  <TextField
                    fullWidth 
                    size="small" 
                    label="Relationship" 
                    value={dep.relationship}
                    onChange={(e) => updateDep(i, 'relationship', e.target.value)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  />
                  <CustomDatePicker
                    variant="outlined"
                    size="small"
                    label="Date of Birth" 
                    name="birthDate"
                    value={dep.birthDate ? new Date(dep.birthDate) : null}
                    onChange={(_, d) => updateDep(i, 'birthDate', d)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  />
                  <TextField
                    fullWidth 
                    size="small" 
                    label="National ID" 
                    value={dep.nationalIdNumber}
                    onChange={(e) => updateDep(i, 'nationalIdNumber', e.target.value)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  />
                  <CustomDatePicker
                    variant="outlined"
                    size="small"
                    label="Valid Since" 
                    name="validSince"
                    value={dep.validSince ? new Date(dep.validSince) : null}
                    onChange={(_, d) => updateDep(i, 'validSince', d)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  />
                  <CustomDatePicker
                    variant="outlined"
                    size="small"
                    label="Valid Until" 
                    name="validUntil"
                    value={dep.validUntil ? new Date(dep.validUntil) : null}
                    onChange={(_, d) => updateDep(i, 'validUntil', d)}
                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => removeDependent(i)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Family;
